import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FreeResourcesBar from '../components/FreeResourcesBar';
import { CheckCircle, Award, Clock, Users, Calendar, Download, ChevronDown, ChevronUp } from 'lucide-react';

const AIBootcampPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const syllabus = [
    { week: "Week 1-2", title: "Introduction to AI & LLMs", topics: ["Understanding AI fundamentals", "Introduction to Large Language Models", "Setting up development environment", "First chatbot application"] },
    { week: "Week 3-4", title: "Prompt Engineering & RAG", topics: ["Advanced prompt techniques", "Retrieval Augmented Generation (RAG)", "Document Q&A systems", "Building context-aware bots"] },
    { week: "Week 5-6", title: "LangChain Fundamentals", topics: ["LangChain architecture", "Chains and agents", "Memory management", "Custom tools and functions"] },
    { week: "Week 7-8", title: "Agent Orchestration", topics: ["Multi-agent systems", "LangGraph workflows", "Agent collaboration", "Complex task automation"] },
    { week: "Week 9-10", title: "Real-World Projects", topics: ["Travel planning agent", "Resume screening system", "Weather intelligence bot", "Portfolio development"] },
    { week: "Week 11-12", title: "Deployment & Career Prep", topics: ["Production deployment", "Performance optimization", "Resume & interview prep", "Final project presentation"] }
  ];

  const faqs = [
    {
      id: 1,
      question: "What is the AI Applications for Beginners bootcamp?",
      answer: "This is a comprehensive 12-week program that teaches you to build intelligent AI applications from scratch. You'll learn about LLMs, RAG workflows, agent orchestration, and build portfolio-ready projects including a travel-planning agent, resume screener, and smart weather assistant. No prior coding experience required!"
    },
    {
      id: 2,
      question: "Are there any prerequisites?",
      answer: "No! This bootcamp is designed for complete beginners. We start with the fundamentals and build up to advanced concepts. You just need a computer, internet connection, and enthusiasm to learn."
    },
    {
      id: 3,
      question: "What if I am unable to attend a session? Will I get the recording?",
      answer: "Yes! All live sessions are recorded and made available within 24 hours. You'll have lifetime access to all recordings, so you can learn at your own pace and revisit concepts anytime."
    },
    {
      id: 4,
      question: "What's the refund policy?",
      answer: "We offer a 14-day money-back guarantee. If you're not satisfied within the first two weeks, we'll provide a full refund, no questions asked."
    },
    {
      id: 5,
      question: "Will I get a certificate?",
      answer: "Yes! Upon successful completion of the bootcamp and final project, you'll receive a professional certificate that you can showcase on LinkedIn and your resume."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <FreeResourcesBar />

      {/* Hero Section - Elegant Palette */}
      <section className="pt-24 pb-16 bg-[#1E293B] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#4F46E5] text-white px-6 py-2 rounded-full font-bold mb-6">
              <Award className="w-5 h-5" />
              <span>12-Week Intensive Program</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              AI Applications for Beginners Bootcamp
            </h1>

            {/* Description */}
            <p className="text-xl md:text-2xl text-slate-700 mb-8 leading-relaxed">
              Prepare to win in the age of agentic AI. Learn about LLMs, RAG workflows, and agent orchestration while building a travel-planning agent, resume screener, and smart weather assistant in this 12-week bootcamp.
            </p>

            <p className="text-lg text-slate-600 mb-8">
              <strong>Open to all! No prior coding experience required.</strong>
            </p>

            {/* Key Info Badges */}
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">Starts November 6, 2025</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">3 sessions per week</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">Limited to 50 students</span>
                </div>
              </div>
            </div>

            {/* Pricing & CTA */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 inline-block">
              <div className="text-5xl font-bold mb-2">$500</div>
              <p className="text-slate-700 mb-6">Includes a year of Codecademy Pro</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => window.location.href = '/contact'}
                  className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-10 py-4 rounded-xl shadow-lg transition-all duration-300"
                >
                  Reserve your seat now
                </button>
                <button className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-bold px-10 py-4 rounded-xl border border-white/20 transition-all duration-300">
                  <Download className="w-5 h-5 inline mr-2" />
                  Download brochure
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Clock, title: "12 WEEKS OF LIVE VIRTUAL SESSIONS", desc: "Interactive learning with expert instructors" },
              { icon: Award, title: "5+ PORTFOLIO READY PROJECTS", desc: "Build real applications you can showcase" },
              { icon: Users, title: "1 YEAR OF CODECADEMY PRO", desc: "Continue learning after bootcamp ends" },
              { icon: CheckCircle, title: "EXPERT CAREER GUIDANCE", desc: "Resume reviews and interview preparation" }
            ].map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Learn to build Agentic AI applications through live, hands-on learning
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80" alt="Live sessions" className="w-full h-48 object-cover rounded-xl mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">Attend live virtual sessions</h3>
              <p className="text-slate-600">Join interactive sessions 3 times a week with your instructor and peers. Ask questions, collaborate, and learn in real-time.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80" alt="Career guidance" className="w-full h-48 object-cover rounded-xl mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">Get real-world career guidance</h3>
              <p className="text-slate-600">Receive personalized resume reviews, mock interviews, and career counseling from industry professionals.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80" alt="Codecademy Pro" className="w-full h-48 object-cover rounded-xl mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">Keep growing with Codecademy Pro</h3>
              <p className="text-slate-600">Enjoy a full year of Codecademy Pro access to continue your learning journey beyond the bootcamp.</p>
            </div>
          </div>

          {/* Projects Section */}
          <div className="bg-teal-700 rounded-3xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-8 text-center">Build portfolio-worthy projects</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: "Document Q&A Chatbot", desc: "Build an intelligent chatbot that can answer questions based on your documents using RAG technology." },
                { title: "Smart Weather Assistant", desc: "Create an AI agent that provides personalized weather insights and travel recommendations." },
                { title: "Personal Travel Planner", desc: "Develop a multi-agent system that plans complete travel itineraries based on user preferences." }
              ].map((project, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h4 className="text-xl font-bold mb-3">{project.title}</h4>
                  <p className="text-teal-100">{project.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Syllabus */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Bootcamp Syllabus</h2>
            <p className="text-xl text-slate-600">12 weeks of structured, hands-on learning</p>
          </div>

          <div className="space-y-4">
            {syllabus.map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-bold text-teal-700">{item.week}</span>
                      <h3 className="text-xl font-bold text-white">{item.title}</h3>
                    </div>
                    <ul className="space-y-1">
                      {item.topics.map((topic, topicIdx) => (
                        <li key={topicIdx} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-10 py-4 rounded-xl shadow-lg transition-all duration-300">
              <Download className="w-5 h-5 inline mr-2" />
              Download the full roadmap
            </button>
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Is this bootcamp right for you?</h2>
            <p className="text-xl text-slate-600">We want to ensure you have the best learning experience possible</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: "🤔", title: "You're curious about agentic AI", desc: "No matter your background, if you're interested in AI and want to build intelligent applications" },
              { icon: "👥", title: "You crave community", desc: "You learn best when surrounded by like-minded peers and supportive instructors" },
              { icon: "💼", title: "You want real-world knowledge", desc: "You're looking for practical skills and industry insights that translate directly to job opportunities" }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-slate-50 to-teal-50 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Prepare for new career opportunities</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: "📝", title: "Review your resume", desc: "Get professional feedback on your resume to stand out to employers" },
              { icon: "🎯", title: "Prepare for interviews", desc: "Practice with mock interviews and learn to showcase your AI skills" },
              { icon: "🚀", title: "Plan your career journey", desc: "Work with career counselors to map your path in the AI industry" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg text-center">
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently asked questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-xl overflow-hidden border border-slate-200">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-slate-900">{faq.question}</span>
                  {openFAQ === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                  )}
                </button>
                {openFAQ === faq.id && (
                  <div className="px-6 py-4 bg-slate-50">
                    <p className="text-slate-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#1E293B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Reserve your spot for AI Applications for Beginners bootcamp
          </h2>
          <p className="text-xl text-slate-700 mb-8">Starting November 6, 2025 | Limited to 50 students</p>
          <button
            onClick={() => window.location.href = '/contact'}
            className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-12 py-5 rounded-xl shadow-2xl transition-all duration-300 text-lg"
          >
            Register now →
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIBootcampPage;
